import { useContext, useEffect, useRef } from 'react';
import { useVesselStore } from '../context/VesselContext';
import { useAppStore } from '../context/AppContext';
import useGetVessel from '../api/useGetVessel';
import getContext from '../context/AppSettingsContext';
import useSignalRConnection from './useSignalRConnection';
import { HubConnectionState } from '@microsoft/signalr';
import useGetMasterPlanApi from 'src/api/stowage/useGetMasterPlanApi';
import { useLocation } from 'react-router-dom';
import useAppFeature from './useAppFeature';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import useGetTankersEventsForCargoes from 'src/api/useGetTankersEventsForCargoes';
import { getDateNow } from 'odfjell-date-time-utils/dateTimeUtils';
import { useEventStore } from 'src/context/EventContext';
import useUpdateMissingTasksIcon from './useUpdateMissingTasksIcon';
import useCargoTempLogApi from 'src/api/cargo-temp-log/useCargoTempLogApi';
import { getFormattedCargoNumber } from 'src/utils/stowage/cargoOperationUtils';
import { getCurrentPortCall } from 'src/utils/stowage/stowageRotationUtils';
import usePreloadVesselData from 'src/hooks/usePreloadVesselData';
import { useConnectionStore } from 'src/context/ConnectionContext';
import { OfflineLevelEnum } from 'src/enums/offlineLevelEnum';
import { useAppStatus } from 'src/context/AppStatusContext';

const SetDataComponent = () => {
    const appSettings = useContext(getContext());
    const location = useLocation();

    const { selectedVessel, selectedListVessel } = useVesselStore();
    const { loadedMasterStowagePlan, setLoadedMasterStowagePlan, setMasterPlanLoadState, setMasterPlanLoadStateImo } = useStowagePlanStore();
    const { cargoOperationsEvents } = useEventStore();
    const { hubConnection, offlineLevel } = useConnectionStore();
    const { apiUnavailable } = useAppStatus();
    const { appFeatureSettings } = useAppStore();

    const { setupSignalRConnection, stopSignalRConnection } = useSignalRConnection();
    const { getVessel } = useGetVessel();
    const { getMasterStowagePlanFromApi } = useGetMasterPlanApi();
    const { isFeatureEnabled } = useAppFeature();
    const { getEventListForCargoes } = useGetTankersEventsForCargoes();
    const { updateCargoTempLogMissingTasks } = useUpdateMissingTasksIcon();
    const { getCargoTemperatureConfigs } = useCargoTempLogApi();
    const { applyPreloadedDataIfMatch } = usePreloadVesselData();

    // Several independent effects below (vessel selection, connectivity restore, API recovery, and
    // tab-visibility wake) can each decide a SignalR reconnect is needed at nearly the same moment
    // — most notably right after a tab wakes from sleep, where "back online" and "tab visible"
    // browser events tend to fire together. Without coordination, each effect would build its own
    // brand-new HubConnection and independently join the vessel group, so a single server broadcast
    // would be delivered once per redundant connection (e.g. 3 duplicate "user has updated" toasts
    // for one real change). This shared debounce collapses near-simultaneous triggers into a single
    // actual reconnect.
    const RECONNECT_DEBOUNCE_MS = 3000;
    const lastReconnectAttemptAtRef = useRef(0);

    const attemptSignalRReconnect = (imoNumber: number, force = false) => {
        const now = Date.now();
        if (now - lastReconnectAttemptAtRef.current < RECONNECT_DEBOUNCE_MS) {
            console.log('Skipping SignalR reconnect attempt — another reconnect just ran (debounced)');
            return;
        }
        lastReconnectAttemptAtRef.current = now;

        if (force) {
            // Await the old connection's full teardown (including explicitly leaving its SignalR
            // group) before creating the new one. Without this, the new connection could join the
            // group while the old one was still registered server-side, causing a single broadcast
            // to be delivered twice (once per connection) to the same client.
            stopSignalRConnection().finally(() => setupSignalRConnection(appSettings.api + '', imoNumber));
        } else {
            setupSignalRConnection(appSettings.api + '', imoNumber);
        }
    };

    // Effect 1: Vessel data fetch — triggered by selectedListVessel (set by RouterContent)
    // This bridges the gap: selectedListVessel → getVessel() → sets selectedVessel
    useEffect(() => {
        if (selectedListVessel) {
            // Try to use preloaded vessel data first, only fetch if not available
            applyPreloadedDataIfMatch(selectedListVessel.imoNumber).then((applied) => {
                if (!applied) {
                    // Preloaded data doesn't match or isn't available, fetch normally
                    getVessel(selectedListVessel.imoNumber);
                }
            });
        }
    }, [selectedListVessel]);

    // Effect 2: SignalR connection lifecycle (vessel only)
    useEffect(() => {
        if (selectedVessel && (!hubConnection || hubConnection.state === HubConnectionState.Disconnected)) {
            attemptSignalRReconnect(selectedVessel.imoNumber);
        }
        return () => {
            stopSignalRConnection();
        };
    }, [selectedVessel]);

    // Effect: Reconnect SignalR when connectivity is restored after dormant period
    useEffect(() => {
        if (
            selectedVessel &&
            offlineLevel === OfflineLevelEnum.Online &&
            (!hubConnection || hubConnection.state === HubConnectionState.Disconnected)
        ) {
            attemptSignalRReconnect(selectedVessel.imoNumber);
        }
    }, [offlineLevel]);

    // Effect: Reconnect SignalR when API recovers (apiUnavailable transitions false)
    useEffect(() => {
        if (!apiUnavailable && selectedVessel && (!hubConnection || hubConnection.state === HubConnectionState.Disconnected)) {
            attemptSignalRReconnect(selectedVessel.imoNumber);
        }
    }, [apiUnavailable]);

    // Effect: Reconnect SignalR when the tab becomes visible again after being hidden long enough
    // to plausibly have been suspended/throttled by the browser (e.g. Edge Sleeping Tabs, Chrome
    // background tab throttling). While hidden, the browser can freeze JS timers — including
    // SignalR's own keep-alive/reconnect timers — so the server may evict the connection from its
    // group without the client ever noticing. In that "zombie connection" state,
    // hubConnection.state can still incorrectly report Connected, so we can't rely on the same
    // Disconnected-state check used by the other reconnect effects above; instead we force a full
    // stop + reconnect unconditionally whenever the tab was hidden long enough. The shared debounce
    // in attemptSignalRReconnect prevents this from stacking a second connection on top of one
    // already being (re)established by the offlineLevel/apiUnavailable effects above, which
    // otherwise commonly fire at the same moment a tab wakes up.
    useEffect(() => {
        const VISIBILITY_RECONNECT_THRESHOLD_MS = 10000;
        let hiddenAt: number | null = null;

        const handleVisibilityChange = () => {
            if (document.hidden) {
                hiddenAt = Date.now();
                return;
            }

            if (hiddenAt === null) return;
            const hiddenDurationMs = Date.now() - hiddenAt;
            hiddenAt = null;

            if (hiddenDurationMs < VISIBILITY_RECONNECT_THRESHOLD_MS || !selectedVessel) return;

            console.log(
                `Tab visible again after ${hiddenDurationMs}ms hidden — forcing SignalR reconnect to recover from a possible tab-sleep disconnect`
            );
            attemptSignalRReconnect(selectedVessel.imoNumber, true);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [selectedVessel]);

    // Effect 3: Stowage plan loading (depends on full vessel data + location + feature settings + connectivity)
    // appFeatureSettings and offlineLevel must both be deps — isFeatureEnabled() reads
    // both (see useAppFeature.ts), and offlineLevel defaults to 'Offline' before the first
    // connectivity check resolves. If either arrives after the vessel already resolved,
    // isFeatureEnabled() reads false on the first run and this effect would never re-fire
    // (selectedVessel/location.pathname unchanged), leaving masterPlanLoadState stuck at 'idle'
    // forever. Since checkLoadedStowageIntegration now blocks on 'idle' too, that stale state
    // would otherwise block callers permanently.
    useEffect(() => {
        if (selectedVessel) {
            // Only fetch the master stowage plan if we're not on the stowage page
            // (inactive vessels may still have historical/current master plans)
            const currentPath = location.pathname;
            if (loadedMasterStowagePlan?.imoNumber !== selectedVessel.imoNumber) {
                setLoadedMasterStowagePlan(null);
            }
            if (
                isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber: selectedVessel.imoNumber, date: getDateNow() }) &&
                currentPath !== '/stowage'
            ) {
                // Set 'loading' synchronously so consumers (e.g. Events page) block until the plan
                // settles before firing getRotation. The API hook transitions to the terminal state.
                // Tag the readiness state with the vessel it belongs to so a stale terminal state
                // from a previous vessel can never be misread as ready for this vessel.
                setMasterPlanLoadState('loading');
                setMasterPlanLoadStateImo(selectedVessel.imoNumber);
                getMasterStowagePlanFromApi(selectedVessel);
            }
        }
    }, [selectedVessel, location.pathname, appFeatureSettings, offlineLevel]);

    useEffect(() => {
        if (loadedMasterStowagePlan && loadedMasterStowagePlan.cargoes && loadedMasterStowagePlan.cargoes.length > 0) {
            const cargoIds = loadedMasterStowagePlan.cargoes
                .filter((cargo) => cargo.imosCargoId != null)
                .map((cargo) => cargo.imosCargoId.toString())
                .filter((cargoId) => !isNaN(Number(cargoId)));
            if (cargoIds.length > 0) {
                getEventListForCargoes(cargoIds);

                // Only load cargo temp configs for current and previous voyage
                const currentPortCall = getCurrentPortCall(loadedMasterStowagePlan);

                if (currentPortCall) {
                    const currentVoyage = Number(currentPortCall.voyageNumber);
                    const previousVoyage = currentVoyage - 1;

                    const relevantCargoes = loadedMasterStowagePlan.cargoes.filter((cargo) => Number(cargo.voyageNumber) >= previousVoyage);

                    if (selectedVessel && relevantCargoes.length > 0) {
                        getCargoTemperatureConfigs(
                            selectedVessel.imoNumber,
                            relevantCargoes.map((cargo) => getFormattedCargoNumber(cargo.voyageNumber, cargo.cargoNumber, false)).join(','),
                            false
                        );
                    }
                }
            }
        }
    }, [loadedMasterStowagePlan]);

    useEffect(() => {
        if (cargoOperationsEvents && cargoOperationsEvents.length > 0 && selectedVessel) {
            updateCargoTempLogMissingTasks(selectedVessel.imoNumber);
        }
    }, [cargoOperationsEvents]);

    return {};
};

export default SetDataComponent;
